/**
 * The four stages of the sentinel design flow, and what each of them is doing.
 *
 * Kept free of React and of any fetching so the rules can be read in one place
 * and tested directly. The inputs are deliberately a flat snapshot rather than
 * the hooks that produce them: the same rules have to reach the same verdict
 * from a live stream and from a page that has only the Rolodex entry to go on.
 */

export const SENTINEL_STAGES = ['context', 'plan', 'run', 'evaluate'] as const

export type SentinelStageId = (typeof SENTINEL_STAGES)[number]

export const STAGE_LABELS: Record<SentinelStageId, string> = {
  context: 'Context',
  plan: 'Plan',
  run: 'Run',
  evaluate: 'Evaluate',
}

/**
 * `blocked` is not a failure: it is a stage whose input does not exist yet, and
 * it is separate from `pending` because the two need different words on screen
 * ("nothing to plan from" versus "not started"). `failed` covers a stalled run
 * too — from this side there is no difference between a job that reported an
 * error and one that stopped reporting anything.
 */
export type SentinelStageState =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'blocked'

export interface SentinelStageStatus {
  state: SentinelStageState
  /** The one line a collapsed stage shows. */
  summary: string
}

export type SentinelStageStatuses = Record<SentinelStageId, SentinelStageStatus>

/**
 * Everything the stage rules read. Each field is a fact about the flow, not
 * about who supplied it — `hasPlan` is true whether the plan came back on the
 * design stream a second ago or was read off `plan.json` after a reload.
 */
export interface SentinelFlowSnapshot {
  /** No instance picked yet: every stage is waiting on the user, not on work. */
  instanceSelected: boolean

  contextLoading: boolean
  contextError: boolean
  hasContext: boolean
  /** The eligibility gate. An ineligible instance cannot be planned from. */
  contextEligible: boolean
  contextTagCount: number

  designLoading: boolean
  designError: boolean
  hasPlan: boolean
  /** A plan missing a target, drivers or methods cannot be run. */
  planRunnable: boolean
  planTarget?: string
  planDriverCount: number

  /** Absent until a run entry exists for this flow. */
  runState?: 'running' | 'stalled' | 'done' | 'error'
  runStarting: boolean
  runError: boolean
  /** The job's own word for what it is doing, when it has said one. */
  runStageLabel?: string

  evaluateLoading: boolean
  evaluateError: boolean
  hasEvaluation: boolean
  evaluationVerdict?: string
}

const contextStatus = (flow: SentinelFlowSnapshot): SentinelStageStatus => {
  if (flow.contextLoading) {
    return { state: 'running', summary: 'Pulling tags, stats and plots…' }
  }
  if (flow.contextError) {
    return { state: 'failed', summary: 'The context build failed.' }
  }
  if (!flow.hasContext) {
    return flow.instanceSelected
      ? { state: 'pending', summary: 'Not built yet.' }
      : { state: 'blocked', summary: 'Pick an asset instance first.' }
  }
  if (!flow.contextEligible) {
    return {
      state: 'blocked',
      summary: 'This asset instance cannot be modelled.',
    }
  }
  return { state: 'done', summary: `${flow.contextTagCount} tags bundled.` }
}

const planStatus = (
  flow: SentinelFlowSnapshot,
  context: SentinelStageStatus
): SentinelStageStatus => {
  if (flow.designLoading) {
    return { state: 'running', summary: 'The design agent is planning…' }
  }
  if (flow.designError) {
    return { state: 'failed', summary: 'The design call failed.' }
  }
  if (flow.hasPlan) {
    const target = flow.planTarget ?? 'no target'
    const drivers = `${flow.planDriverCount} driver${
      flow.planDriverCount === 1 ? '' : 's'
    }`
    return flow.planRunnable
      ? { state: 'done', summary: `${target} from ${drivers}.` }
      : {
          state: 'pending',
          summary: `${target} from ${drivers} — not runnable yet.`,
        }
  }
  return context.state === 'done'
    ? { state: 'pending', summary: 'Not designed yet.' }
    : { state: 'blocked', summary: 'Needs a usable context bundle.' }
}

const runStatus = (
  flow: SentinelFlowSnapshot,
  plan: SentinelStageStatus
): SentinelStageStatus => {
  if (flow.runState === 'running' || flow.runStarting) {
    return {
      state: 'running',
      summary: flow.runStageLabel
        ? `Training — ${flow.runStageLabel}.`
        : 'Training…',
    }
  }
  if (flow.runState === 'error') {
    return { state: 'failed', summary: 'The training job failed.' }
  }
  if (flow.runState === 'stalled') {
    return { state: 'failed', summary: 'The training job stopped reporting.' }
  }
  if (flow.runState === 'done') {
    return { state: 'done', summary: 'Fitted and saved.' }
  }
  if (flow.runError) {
    return { state: 'failed', summary: 'The run could not be started.' }
  }
  return plan.state === 'done'
    ? { state: 'pending', summary: 'Not run yet.' }
    : { state: 'blocked', summary: 'Needs a runnable plan.' }
}

const evaluateStatus = (
  flow: SentinelFlowSnapshot,
  run: SentinelStageStatus
): SentinelStageStatus => {
  if (flow.evaluateLoading) {
    return { state: 'running', summary: 'Judging the residuals…' }
  }
  if (flow.evaluateError) {
    return { state: 'failed', summary: 'The evaluate call failed.' }
  }
  if (flow.hasEvaluation) {
    return { state: 'done', summary: flow.evaluationVerdict || 'Evaluated.' }
  }
  return run.state === 'done'
    ? { state: 'pending', summary: 'Not evaluated yet.' }
    : { state: 'blocked', summary: 'Needs a finished run.' }
}

/** Every stage's state, each one reading only the stage before it. */
export const deriveStageStatuses = (
  flow: SentinelFlowSnapshot
): SentinelStageStatuses => {
  const context = contextStatus(flow)
  const plan = planStatus(flow, context)
  const run = runStatus(flow, plan)
  return { context, plan, run, evaluate: evaluateStatus(flow, run) }
}

/**
 * Where to land someone who has just opened this flow.
 *
 * A stage that is doing something wins, because that is the thing they came to
 * watch; then the earliest one that has gone wrong, because that is the thing
 * they have to deal with; then the furthest one they can act on. A completed
 * flow lands on its last stage rather than its first.
 */
export const defaultStage = (
  statuses: SentinelStageStatuses
): SentinelStageId => {
  const byState = (state: SentinelStageState): SentinelStageId | undefined =>
    SENTINEL_STAGES.find((stage) => statuses[stage].state === state)
  const actionable =
    byState('running') ??
    byState('failed') ??
    byState('pending') ??
    byState('blocked')
  if (actionable) return actionable
  return SENTINEL_STAGES[SENTINEL_STAGES.length - 1]
}

/**
 * Whether a stage is worth opening. A blocked stage has nothing in it yet, so
 * the rail shows its state but does not pretend there is a panel behind it.
 */
export const isStageReviewable = (status: SentinelStageStatus): boolean =>
  status.state !== 'blocked'
